// InternationalMarketingPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/strategyxs.png'

const InternationalMarketingPage = () => {
    window.scroll(0, 0)

    useEffect(() => {
        window.scroll(0, 0)
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const internationalStats = [
        { number: '🌍', label: 'Reach new customer segments across multiple countries' },
        { number: '📈', label: 'Grow organic traffic and conversions globally' },
        { number: '🧠', label: 'Understand audience behaviour in local contexts' },
        { number: '📊', label: 'Optimise campaigns based on cross-market performance data' }
    ];

    const internationalApproaches = [
        {
            icon: 'fas fa-search',
            title: 'Global Strategy & Market Analysis',
            desc: 'We begin by understanding your target markets and audience segments — including behaviours, competitive landscapes, and cultural preferences — to inform a tailored international growth strategy. Includes market and cultural segmentation, local search behaviour insights, and regional competitor evaluation.'
        },
        {
            icon: 'fas fa-globe',
            title: 'Localised SEO & Search Marketing',
            desc: 'Global visibility starts with being discoverable in local search environments. We optimise content and search presence to ensure your brand ranks naturally across different languages and regions. Includes multi-language keyword research, localised content optimisation, and technical SEO for international domains (hreflang, geo-targeting).'
        },
        {
            icon: 'fas fa-ad',
            title: 'Paid Media & Performance Advertising',
            desc: 'We plan and execute paid campaigns that are tailored to each market\'s unique behaviour and platform preferences — driving engagement and conversions efficiently. Includes search and display ads, paid social campaigns, and local bidding and budgeting strategy.'
        },
        {
            icon: 'fas fa-share-alt',
            title: 'Social & Content Marketing Across Borders',
            desc: 'Engagement means speaking your audience\'s language — literally and culturally. We create campaigns and content that connect with users on their preferred platforms and in ways that matter. Includes social storytelling for local audiences, region-specific content strategies, and multi-platform distribution plans.'
        },
        {
            icon: 'fas fa-chart-bar',
            title: 'Analytics, Reporting & Growth Optimisation',
            desc: 'We provide transparent performance tracking and insights, empowering you to measure what matters and optimise campaigns for long-term global impact. Includes cross-market performance dashboards, behavioural and conversion analysis, and continuous optimisation recommendations.'
        }
    ];

    const benefits = [
        ['Local Market Expertise', 'Access deep cultural and behavioural insights for each target region'],
        ['Scalable Growth Framework', 'Expand efficiently with proven international marketing models'],
        ['Unified Brand Consistency', 'Maintain brand integrity while adapting to local preferences'],
        ['Risk Mitigation & Compliance', 'Navigate local regulations, data privacy, and advertising standards'],
        ['Measurable Global ROI', 'Track performance with transparent cross-market dashboards and KPIs']
    ];

    const targetBusinesses = [
        'Brands entering new geographic markets',
        'Companies scaling internationally from a domestic base',
        'E-commerce and SaaS businesses with global customers',
        'Organisations targeting multi-language audiences',
        'Businesses in competitive international landscapes',
        'Organizations planning to expand their digital footprint across borders'
    ];

    const internationalQuestions = [
        'How can we adapt our brand message to resonate across different cultures?',
        'Which international markets offer the highest growth potential for our products?',
        'How do local search behaviours and platform preferences differ by region?',
        'What is the most efficient way to allocate budget across global campaigns?',
        'How does our digital performance compare to local competitors in each market?'
    ];

    return (
        <div className="strategy sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">International Digital Marketing</span> Agency
                    </h1>
                    <p>Expand Your Brand's Global Reach with Strategic, Data-Led Campaigns</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={12}>
                                <p className="sv-lead">
                                    Expanding your digital presence beyond domestic borders requires more than simply translating campaigns or targeting broader audiences. Reachtix's <span className="sv-keyword-highlight">International Digital Marketing Agency</span> services are designed to help brands connect authentically with audiences in multiple countries and cultures, driving growth through tailored digital strategies and global performance optimisation.
                                </p>
                                <p className="sv-description">
                                    As an agency with deep expertise in international markets, we combine global insight with local execution to deliver campaigns that resonate — wherever your customers are searching online.
                                </p>
                                <p className="sv-description">
                                    <strong>What Is International Digital Marketing?</strong> International digital marketing involves promoting your products or services in markets outside your home country by leveraging digital channels such as SEO, paid media, social advertising, content marketing, and analytics. It's about creating strategies that reflect local language, cultural nuances, and market behaviour while maintaining brand consistency on a global scale. Expanding globally isn't just about broadening reach — it's about being meaningful and relevant to audiences in each market you target.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color="dark"
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Your Global Expansion
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color="dark"
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free International Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Global Digital Marketing</span> Is Critical
                        </h2>
                        <Row className="sv-stats-grid">
                            {internationalStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is International Marketing Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is International Digital Marketing?</h2>
                                <p className="sv-section-description">
                                    <strong>International digital marketing</strong> involves promoting your products or services in markets outside your home country by leveraging digital channels such as SEO, paid media, social advertising, content marketing, and analytics. It's about creating strategies that reflect local language, cultural nuances, and market behaviour while maintaining brand consistency on a global scale.
                                </p>
                                <p className="sv-section-description">
                                    Expanding globally isn't just about broadening reach — it's about being meaningful and relevant to audiences in each market you target. Today's digital landscape is interconnected, yet diverse. What works in one region may not resonate in another, making international marketing both an opportunity and a challenge.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions Our International Marketing Strategy Answers:</h4>
                                    {internationalQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why International Marketing Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Why Global Digital Marketing Drives Success</h2>
                                <p className="sv-section-description">
                                    With an international strategy in place, your business can unlock new opportunities and achieve sustainable growth across borders through effective <strong>global marketing</strong>.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-globe-americas"></i>
                                        <h5>Reach New Customer Segments</h5>
                                        <p>Expand your audience across multiple countries and unlock untapped markets worldwide</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Grow Organic Traffic & Conversions</h5>
                                        <p>Drive sustainable global growth through localised search and content strategies</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-brain"></i>
                                        <h5>Understand Local Behaviour</h5>
                                        <p>Gain deep insights into audience preferences and cultural nuances in each market</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-pie"></i>
                                        <h5>Optimise Cross-Market Campaigns</h5>
                                        <p>Use performance data to allocate budget and resources efficiently across regions</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our International Marketing Expertise Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Our International Digital Marketing Expertise</h2>
                                <p className="sv-section-description">
                                    At Reachtix, we blend strategic planning, local knowledge, and advanced digital capabilities to deliver high-performance global marketing solutions.
                                </p>

                                <div className="sv-approaches-grid">
                                    {internationalApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Benefits of Working with Reachtix Globally</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Benefit</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([benefit, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{benefit}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Who Needs International Digital Marketing?</h2>
                                <p className="sv-section-description">
                                    Our <strong>international digital marketing services</strong> deliver maximum value for businesses seeking global growth, clarity, and sustainable expansion:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you are launching new offerings abroad or strengthening your global footprint, we help you grow with clarity and confidence. A <strong>global marketing strategy</strong> is valuable whether your brand is entering new markets or scaling existing international operations.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Partner Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Partner with Reachtix?</h2>
                                <p className="sv-section-description">
                                    Choosing the right international digital marketing partner means accessing global strategy expertise, local insight, and measurable impact.
                                </p>
                                <p className="sv-section-description">
                                    At Reachtix, we combine deep international market knowledge, proven digital marketing frameworks, data-driven performance optimisation, and tailored campaigns for each audience and region. We help your brand not only expand, but thrive on the global stage.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <h2>Ready to Grow Your Brand Internationally?</h2>
                        <p className="sv-cta-description">
                            Global growth begins with a strategy rooted in insight, localisation, and adaptability. Reachtix's International Digital Marketing Agency services help you enter new markets and build sustainable digital success worldwide.
                        </p>
                        <p className="sv-cta-description">
                            Let Reachtix guide your global expansion journey with strategic expertise, local market knowledge, and data-driven campaigns that deliver real results across borders.
                        </p>
                        <div className="sv-cta-buttons">
                            <Button
                                tag={Link}
                                to="/contact"
                                color="primary"
                            >
                                <i className="fas fa-globe mr-2"></i>
                                Start Your International Growth Journey
                            </Button>
                            <Button
                                tag={Link}
                                to="/contact"
                                color="light"
                            >
                                <i className="fas fa-comments mr-2"></i>
                                Contact Reachtix Today
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default InternationalMarketingPage;